import { ConnectProto } from "@racoon/api";
import type { StatusTone } from "@racoon/lib";

import { isSetId, type BucketKey } from "./pickup-status.lib";
import type { BucketedJob } from "./use-pickup-status";

const S = ConnectProto.JobStatus;

/** The tones a site pin can take, most urgent first: a site shows its most urgent job. */
export const SITE_TONES = ["failed", "pending", "active", "assigned", "done"] as const;
export type SiteTone = (typeof SITE_TONES)[number] & StatusTone;

export function jobTone(job: ConnectProto.Job, bucket: BucketKey): SiteTone {
  switch (bucket) {
    case "missed":
      return "failed";
    case "unassigned":
      return "pending";
    case "done":
      return "done";
  }
  return job.status === S.ENROUTE || job.status === S.ON_SERVICE ? "active" : "assigned";
}

export type SiteJob = BucketedJob & { tone: SiteTone };

export type SitePin = {
  id: string;
  lat: number;
  lon: number;
  name: string;
  tone: SiteTone;
  geometry?: unknown;
  /** Most urgent first. */
  jobs: SiteJob[];
};

const rank = (tone: SiteTone) => SITE_TONES.indexOf(tone);

export const byUrgency = (a: SiteJob, b: SiteJob) =>
  rank(a.tone) - rank(b.tone) || Number(a.job.id - b.job.id);

/** One pin per site with jobs; sites never placed on the map, at (0,0), are left out. */
export function buildSitePins(jobs: BucketedJob[]): SitePin[] {
  const sites = new Map<string, SitePin>();
  for (const { job, bucket } of jobs) {
    const pl = job.pickupLocation;
    if (!pl || !Number.isFinite(pl.lat) || !Number.isFinite(pl.lon) || (!pl.lat && !pl.lon)) {
      continue;
    }
    const id = String(isSetId(pl.id) ? pl.id : job.pickupLocationId);
    const tone = jobTone(job, bucket);
    const site = sites.get(id);
    if (site) {
      site.jobs.push({ job, bucket, tone });
      site.geometry ??= pl.geometry;
      if (rank(tone) < rank(site.tone)) site.tone = tone;
    } else {
      sites.set(id, {
        id,
        lat: pl.lat,
        lon: pl.lon,
        name: pl.name,
        tone,
        geometry: pl.geometry,
        jobs: [{ job, bucket, tone }],
      });
    }
  }
  for (const site of sites.values()) {
    site.jobs.sort(byUrgency);
  }
  return [...sites.values()];
}
